import { RoomMember, type MatrixEvent, type Room } from "matrix-js-sdk/src/matrix";

import { _t } from "../../languageHandler";
import { ContactStateManager } from "../../customisations/ContactStateManager";
import { type ContactFormatEnum } from "../../dth/ContactFormatEnum";
import { getDmMember } from "../../dth/utils";

/**
 * Ritorna il nome del mittente dell'evento, disambiguando se necessario. (es. "Alice (@alice:example.com)").
 * @param sender Il mittente dell'evento da cui estrarre il nome. Se non è disponibile, viene usato l'userId come fallback.
 * @param userId L'userId da usare come fallback se il mittente non è disponibile. Se non è disponibile, viene usato "someone" come fallback finale.
 * @returns Il nome del mittente dell'evento, disambiguato se necessario.
 */
export function getSenderNameFromMatrixEvent(event: MatrixEvent): string {
    return getRoomMemberName(event.sender, event.getSender() || event.sender?.userId);
}

/**
 * Ritorna il nome del destinatario dell'evento, disambiguando se necessario. (es. "Alice (@alice:example.com)").
 * @param sender Il destinatario dell'evento da cui estrarre il nome. Se non è disponibile, viene usato l'userId come fallback.
 * @param userId L'userId da usare come fallback se il destinatario non è disponibile. Se non è disponibile, viene usato "someone" come fallback finale.
 * @returns Il nome del destinatario dell'evento, disambiguato se necessario.
 */
export function getTargetNameFromMatrixEvent(event: MatrixEvent): string {
    return getRoomMemberName(event.target, event.target?.userId);
}

/**
 * Ritorna il nome del membro della stanza, disambiguando se necessario. (es. "Alice (@alice:example.com)").
 * @param member Il membro della stanza da cui estrarre il nome. Se non è disponibile, viene usato l'userId come fallback.
 * @param userId L'userId da usare come fallback se il membro non è disponibile. Se non è disponibile, viene usato "someone" come fallback finale.
 * @returns Il nome del membro della stanza, disambiguato se necessario.
 */
export function getRoomMemberName(
    member?: RoomMember | null,
    userId?: string | null,
): string {
    const id = member?.userId ?? userId;
    if (!id) return _t("common.someone");
    if (!member) return id;

    // Se il membro non è disambiguato, restituisci semplicemente il suo nome o userId.
    const memberName = member.rawDisplayName || member.name || id;
    if (!member.disambiguate) return memberName;

    // In questo caso, il nome del mittente è ambiguo (ad esempio, "Alice" per più utenti). 
    // Proviamo a "disambiguare" usando l'email altrimenti lascio l'userId.
    const suffix = ContactStateManager.getContactEmailByMatrixName(id, true) ?? id;
    return `${memberName} (${suffix})`;
}

/**
 * Ritorna il nome del membro della stanza formattato secondo le preferenze dell'utente, disambiguando se necessario. (es. "Alice Smith" o "Alice Smith (@alice:example.com)").
 * @param member Il membro della stanza da cui estrarre il nome. Se non è disponibile, viene usato l'userId come fallback.
 * @param userId L'userId da usare come fallback se il membro non è disponibile. Se non è disponibile, viene usato "someone" come fallback finale.
 * @param contactFormat Il formato di visualizzazione preferito per il nome del contatto.
 * @returns Il nome del membro della stanza formattato secondo le preferenze dell'utente, disambiguato se necessario.
 */
export function getRoomMemberNameWithContactFormat(
    member?: RoomMember | null,
    userId?: string | null,
    contactFormat: ContactFormatEnum | null = null
): string {
    const id = member?.userId ?? userId;
    if (!id) return _t("common.someone");
    if (!member) return id;

    const contact = ContactStateManager.getContactByMatrixName(id);
    const memberName = contact ?
        ContactStateManager.getContactsNameFormat([contact], contactFormat) :
        member.rawDisplayName || member.name || id;

    // Se il membro non è disambiguato, restituisci semplicemente il suo nome o userId.
    if (!member.disambiguate) return memberName;

    // In questo caso, il nome del mittente è ambiguo (ad esempio, "Alice" per più utenti). 
    // Proviamo a "disambiguare" usando l'email altrimenti lascio l'userId.
    const suffix = contact?.email ?? id;
    return `${memberName} (${suffix})`;
}

/**
 * Ritorna il nome visualizzato della room:
 * - Se è una chat diretta (DM), ritorna il nome del membro usando getRoomMemberNameWithContactFormat,
 *   gestendo anche la disambiguazione e il formato del contatto.
 * - Altrimenti, ritorna room.name.
 * @param room La room di cui ottenere il nome.
 * @param contactFormat Il formato di visualizzazione preferito per il nome del contatto.
 * @returns Il nome visualizzato della room.
 */
export function getRoomDisplayName(
    room: Room,
    contactFormat: ContactFormatEnum | null = null,
): string {
    const dmMember = getDmMember(room);
    // Se è una chat diretta e il membro ha effettuato l'accesso,
    // ritorna il nome del membro DM formattato con le preferenze dell'utente, altrimenti ritorna il nome della room.
    if (dmMember && dmMember.membership === "join") {
        return getRoomMemberNameWithContactFormat(dmMember, dmMember.userId, contactFormat);
    }
    return room.name;
}